import React from 'react'
import PropTypes from 'prop-types'
import { CloseButton } from 'secondstep-components'
import {
  ADD_INSTRUCTOR_CANCEL,
  ADD_INSTRUCTOR_EMAIL_LABEL,
  ADD_INSTRUCTOR_HEADER,
  ADD_INSTRUCTOR_HELPER,
  ADD_INSTRUCTOR_PEOPLE_LABEL,
  ADD_INSTRUCTOR_SUBMIT,
  ROLE_CO_TEACHER,
  ROLE_OWNER,
} from './constants'
import {
  Avatar,
  AddInstructorCancelButton,
  EmailEntry,
  HelperText,
  LookupErrorBox,
  ModalHeaderInput,
  ModalWrapperAddInstructor,
  PeopleLabel,
  PeopleSection,
  PersonInfo,
  PersonRole,
  PersonRow,
  PersonText,
  ShareButton,
  StyledButtonsWrapper,
  StyledFormInputText,
} from './component.styles'

const getInitial = person => {
  const name = person.firstName || person.email || ''
  return name.charAt(0).toUpperCase()
}

const formatName = person => {
  const fullName = [person.firstName, person.lastName].filter(Boolean).join(' ')
  if (!fullName) return person.email
  return person.isCurrentUser ? `${fullName} (You)` : fullName
}

const getValidationMessage = inputErrors => {
  const errors = inputErrors?.email?.errors
  if (!errors) return null
  return Array.isArray(errors) ? errors[0] : errors
}

const AddInstructor = ({
  dataTestId,
  email,
  handleCancel,
  handleEmailChange,
  handleSubmit,
  inputErrors,
  isSubmitting,
  lookupError,
  peopleWithAccess,
}) => {
  const validationMessage = getValidationMessage(inputErrors)
  const errorMessage = lookupError || validationMessage
  const hasError = !!errorMessage

  return (
    <ModalWrapperAddInstructor dataTestId={dataTestId}>
      <CloseButton dataTestId="button-close" onClick={handleCancel} />
      <ModalHeaderInput dataTestId="modal-add-instructor-header">
        {ADD_INSTRUCTOR_HEADER}
      </ModalHeaderInput>
      <HelperText>{ADD_INSTRUCTOR_HELPER}</HelperText>
      <EmailEntry $hasError={hasError}>
        <StyledFormInputText
          autoComplete={false}
          autofocus
          dataTestId="add-instructor-email"
          inputErrors={{ errors: null }}
          label={ADD_INSTRUCTOR_EMAIL_LABEL}
          maxLength={256}
          name="email"
          onChange={handleEmailChange}
          value={email}
        />
        {hasError && (
          <LookupErrorBox data-testid="add-instructor-lookup-error">
            <p>{errorMessage}</p>
          </LookupErrorBox>
        )}
      </EmailEntry>
      <PeopleSection>
        <PeopleLabel>{ADD_INSTRUCTOR_PEOPLE_LABEL}</PeopleLabel>
        {peopleWithAccess.map(person => (
          <PersonRow key={person.email || person.userId}>
            <PersonInfo>
              <Avatar aria-hidden>{getInitial(person)}</Avatar>
              <PersonText>
                <span className="name">{formatName(person)}</span>
                <span className="email">{person.email}</span>
              </PersonText>
            </PersonInfo>
            <PersonRole>
              {person.role === 'owner' ? ROLE_OWNER : ROLE_CO_TEACHER}
            </PersonRole>
          </PersonRow>
        ))}
      </PeopleSection>
      <StyledButtonsWrapper>
        <AddInstructorCancelButton
          dataTestId="button-cancel"
          onClick={handleCancel}
        >
          {ADD_INSTRUCTOR_CANCEL}
        </AddInstructorCancelButton>
        <ShareButton
          dataTestId="button-share"
          disabled={isSubmitting}
          onClick={handleSubmit}
        >
          {ADD_INSTRUCTOR_SUBMIT}
        </ShareButton>
      </StyledButtonsWrapper>
    </ModalWrapperAddInstructor>
  )
}

AddInstructor.propTypes = {
  dataTestId: PropTypes.string,
  email: PropTypes.string,
  handleCancel: PropTypes.func.isRequired,
  handleEmailChange: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  inputErrors: PropTypes.shape({
    email: PropTypes.object,
  }),
  isSubmitting: PropTypes.bool,
  lookupError: PropTypes.string,
  peopleWithAccess: PropTypes.arrayOf(
    PropTypes.shape({
      email: PropTypes.string,
      firstName: PropTypes.string,
      isCurrentUser: PropTypes.bool,
      lastName: PropTypes.string,
      role: PropTypes.string,
      userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ),
}

AddInstructor.defaultProps = {
  email: '',
  inputErrors: { email: { errors: null } },
  isSubmitting: false,
  lookupError: null,
  peopleWithAccess: [],
}

export default AddInstructor
